import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'react-translate.ts';

import { INotification } from '../../../../types/common/INotification';
import {
    NOTIFICATION_TYPE_ERROR,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
} from '../../../../constants/common/notifications.constants';
import { NotificationActions } from '../../../actions/NotificationActions';
import styles from './Notification.module.scss';
import { cx } from '@emotion/css';
import XIcon from 'react-bootstrap-icons/dist/icons/x';
import { mapParagraphs } from '../../../../helpers/mapParagraphs';

const DEFAULT_NOTIFICATION_TIMEOUT = 5000;

export default function Notification({
    notification,
}: {
    notification: INotification;
}) {
    const dispatch = useDispatch();
    const {
        type,
        message,
        timestamp,
        options = {},
        shouldTranslate = true,
        notificationTimeout = null,
    } = notification;
    const translate = useTranslate();

    const pop = useCallback(
        () => dispatch(NotificationActions.popNotification(timestamp)),
        [dispatch, timestamp],
    );

    useEffect(() => {
        if (options?.noTimeout === true) {
            return;
        }

        const timerId = setTimeout(
            pop,
            notificationTimeout ?? DEFAULT_NOTIFICATION_TIMEOUT,
        );

        return () => clearTimeout(timerId);
    }, [pop, options?.noTimeout]);

    const className = resolveNotificationType(type);
    return (
        <div className={cx(styles.notification, className)}>
            <div className={styles.message}>
                {mapParagraphs(shouldTranslate ? translate(message) : message)}
            </div>
            <span className={cx(styles.close)} onClick={pop}>
                <XIcon />
            </span>
        </div>
    );
}

function resolveNotificationType(type: string): string {
    switch (type) {
        case NOTIFICATION_TYPE_ERROR:
            return styles.error;
        case NOTIFICATION_TYPE_SUCCESS:
            return styles.success;
        case NOTIFICATION_TYPE_WARNING:
            return styles.warning;
        default:
            return styles.info;
    }
}
